import { Button } from "materia"
import { useId, useState } from "react"
import { useTranslation } from "react-i18next"
import { PeerAvatarGroup } from "../PeerAvatar"
import type { Colleague } from "./PeoplePicker"
import s from "./GroupName.module.scss"

/** How many names the summary spells out before counting the rest. */
const NAMED = 2

type GroupNameProps = {
  members: Colleague[]
  onCreate: (name: string) => void
  /** Back to the people picker, keeping who was chosen. */
  onBack: () => void
  isCreating?: boolean
}

export function GroupName({
  members,
  onCreate,
  onBack,
  isCreating,
}: GroupNameProps) {
  const { t } = useTranslation()
  const [name, setName] = useState("")
  const fieldId = useId()

  const trimmed = name.trim()

  return (
    <div className={s.groupName}>
      <div className={s.groupName__who}>
        <PeerAvatarGroup members={members} size="large" />
        <p className={s.groupName__summary}>{summary(members, t)}</p>
      </div>

      <label className={s.groupName__label} htmlFor={fieldId}>
        {t("chat.GROUP_NAME_LABEL")}
        <input
          id={fieldId}
          className={s.groupName__input}
          value={name}
          placeholder={String(t("chat.GROUP_NAME_PLACEHOLDER"))}
          // biome-ignore lint/a11y/noAutofocus: the step exists to fill in this one field
          autoFocus
          onChange={(event) => setName(event.target.value)}
        />
      </label>

      <div className={s.groupName__actions}>
        <Button theme="cancel" size="big" borderRadius="24" onClick={onBack}>
          {t("actions.BACK")}
        </Button>
        <div className={s.groupName__start}>
          <Button
            theme="blue"
            size="big"
            borderRadius="24"
            disabled={trimmed.length === 0}
            isLoading={isCreating}
            onClick={() => onCreate(trimmed)}
          >
            {t("chat.START_GROUP")}
          </Button>
        </div>
      </div>
    </div>
  )
}

/** "Mike, Jane and 5 more" — the first few names, then a count. */
function summary(
  members: Colleague[],
  t: (key: string, options?: object) => unknown
): string {
  const names = members
    .slice(0, NAMED)
    .map((member) => member.displayName)
    .join(", ")

  const rest = members.length - NAMED
  if (rest <= 0) return names

  return String(t("chat.GROUP_SUMMARY", { names, count: rest }))
}
